import { Link, useNavigate } from 'react-router-dom'
import { Car, Settings, Moon, Sun, Package, Shield, User, LogOut, Building2 } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/Sheet'
import { Switch } from '../ui/Switch'
import { useThemeStore } from '../../stores/theme'
import { useAuthStore } from '../../stores/auth'
import { Separator } from '../ui/Separator'

const moreLinks = [
  { name: 'Vehicle History', icon: Car, href: '/vehicles' },
  { name: 'Catalog', icon: Package, href: '/catalog' },
  { name: 'Settings', icon: Settings, href: '/settings' },
  { name: 'Profile', icon: User, href: '/profile' },
]

interface MoreSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MoreSheet({ open, onOpenChange }: MoreSheetProps) {
  const navigate = useNavigate()
  const setTheme = useThemeStore((s) => s.setTheme)
  const isDark = useThemeStore((s) => s.resolvedTheme) === 'dark'
  const user = useAuthStore((s) => s.user)
  const shops = useAuthStore((s) => s.shops)
  const currentShopId = useAuthStore((s) => s.currentShopId)
  const setCurrentShop = useAuthStore((s) => s.setCurrentShop)
  const logout = useAuthStore((s) => s.logout)
  const currentShop = shops.find((shop) => shop.id === currentShopId)
  const canAccessAdminConsole = Boolean(user?.isShopbayAdmin)

  const handleLogout = async () => {
    onOpenChange(false)
    await logout()
    navigate('/login')
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>More</SheetTitle>
        </SheetHeader>
        <div className="px-4 py-2 space-y-1">
          {user && (
            <div className="rounded-xl border border-border bg-surface-secondary/60 px-3 py-3">
              <p className="text-sm font-semibold text-content-primary">{user.name || 'Account'}</p>
              <p className="text-xs text-content-secondary">{user.email}</p>
              {currentShop && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-border px-2 py-1 text-[11px] text-content-tertiary">
                  <Building2 className="h-3.5 w-3.5" />
                  <span>{currentShop.name}</span>
                  <span className="capitalize">{currentShop.role}</span>
                </div>
              )}
            </div>
          )}

          {moreLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              onClick={() => onOpenChange(false)}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-content-primary hover:bg-surface-tertiary transition-colors"
            >
              <link.icon className="h-5 w-5 text-content-secondary" />
              {link.name}
            </Link>
          ))}
          {canAccessAdminConsole && (
            <Link
              to="/admin"
              onClick={() => onOpenChange(false)}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-content-primary hover:bg-surface-tertiary transition-colors"
            >
              <Shield className="h-5 w-5 text-content-secondary" />
              Admin Console
            </Link>
          )}

          {shops.length > 1 && (
            <>
              <Separator className="my-2" />
              <div className="px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-content-tertiary">
                  Switch Shop
                </p>
              </div>
              {shops.map((shop) => {
                const active = shop.id === currentShopId
                return (
                  <button
                    key={shop.id}
                    onClick={() => {
                      setCurrentShop(shop.id)
                      onOpenChange(false)
                    }}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-surface-tertiary"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-content-primary">{shop.name}</p>
                      <p className="text-xs capitalize text-content-tertiary">{shop.role}</p>
                    </div>
                    <span
                      className={
                        active
                          ? 'rounded-full bg-primary-600/20 px-2 py-1 text-[11px] font-medium text-primary-300'
                          : 'rounded-full border border-border px-2 py-1 text-[11px] text-content-tertiary'
                      }
                    >
                      {active ? 'Active' : 'Switch'}
                    </span>
                  </button>
                )
              })}
            </>
          )}

          <Separator className="my-2" />
          <div className="flex items-center justify-between rounded-lg px-3 py-3">
            <div className="flex items-center gap-3">
              {isDark ? <Moon className="h-5 w-5 text-content-secondary" /> : <Sun className="h-5 w-5 text-content-secondary" />}
              <span className="text-sm font-medium text-content-primary">Dark Mode</span>
            </div>
            <Switch
              checked={isDark}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            />
          </div>
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-danger-500 transition-colors hover:bg-danger-500/10"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
